import { AppInput } from '@components/form/input/app-input';
import { AppModal } from '@components/form/Modal';
import { AppPhoneInput } from '@components/form/phone-input/app-phone-input';
import { AppSelect } from '@components/form/select/app-select';
import { AppTimeRangePicker } from '@components/form/date/app-time-range-picker';
import { capitalize } from '@shared/utils/capitalize';
import { Checkbox, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { PreferredContactMethodType } from '../app-banner/banner.types';
import dayjs from 'dayjs';

const PreferredContactMethodModal = ({
  open,
  onClose,
  control,
  selectedMethods,
  setValue,
  trigger,
  onSubmit,
  clearErrors,
  errors,
}: PreferredContactMethodType) => {
  const { t } = useTranslation();

  const validateContactMethods = () => {
    clearErrors('contactMethods');
    void trigger('contactMethods');
  };

  return (
    <Modal
      className="w-[420px]!"
      centered
      onCancel={onClose}
      open={open}
      title={
        <AppModal.Header>
          <AppModal.Title>Preferred Contact Method</AppModal.Title>
          <AppModal.SubTitle>
            Please provide your preferred support channel to help us assist you faster.
          </AppModal.SubTitle>
        </AppModal.Header>
      }
      footer={
        <AppModal.Footer>
          <AppModal.Button onClick={onClose} className="flex-1">
            {capitalize(t('cancel'))}
          </AppModal.Button>
          <AppModal.Button color="primary" className="flex-1" onClick={onSubmit}>
            Save and Continue
          </AppModal.Button>
        </AppModal.Footer>
      }
    >
      <AppModal.Body>
        <form className="w-full my-4 mb-5 max-h-[230px] overflow-y-auto" onSubmit={onSubmit}>
          <p className="pb-3.5">Preferred support channel</p>

          {errors?.contactMethods?.message && (
            <p className="text-red-500 text-xs pb-3">{errors.contactMethods.message}</p>
          )}

          <div className="space-y-4">
            <div>
              <label className="flex gap-2 cursor-pointer items-start">
                <Checkbox
                  checked={selectedMethods.email}
                  onChange={(e) => {
                    setValue('emailEnabled', e.target.checked);
                    if (!e.target.checked) clearErrors('email');
                    validateContactMethods();
                  }}
                />
                <span>Email</span>
              </label>

              {selectedMethods.email && (
                <div className="mt-2 pl-6">
                  <AppInput name="email" control={control} placeholder="Enter email" />
                </div>
              )}
            </div>
            <div>
              <label className="flex gap-2 cursor-pointer items-start">
                <Checkbox
                  checked={selectedMethods.messaging}
                  onChange={(e) => {
                    setValue('messagingEnabled', e.target.checked);

                    if (e.target.checked) {
                      setValue('messagingType', 'whatsapp');
                    } else {
                      clearErrors(['messaging', 'messagingType']);
                      setValue('messagingType', '');
                      setValue('messaging', '');
                    }

                    validateContactMethods();
                  }}
                />
                <span>Messaging</span>
              </label>

              {selectedMethods.messaging && (
                <div className="mt-2 pl-6 space-y-3">
                  <AppSelect
                    name="messagingType"
                    control={control}
                    options={[
                      { label: 'WhatsApp', value: 'whatsapp' },
                      { label: 'Viber', value: 'viber' },
                      { label: 'Telegram', value: 'telegram' },
                      { label: 'SMS', value: 'sms' },
                    ]}
                    placeholder="Select Messaging Option"
                    className="w-full"
                  />
                  <AppPhoneInput name="messaging" control={control} />
                </div>
              )}
            </div>

            <div>
              <label className="flex gap-2 cursor-pointer items-start">
                <Checkbox
                  checked={selectedMethods.phoneCall}
                  onChange={(e) => {
                    setValue('phoneCallEnabled', e.target.checked);

                    if (!e.target.checked) {
                      clearErrors('phoneCall');
                      setValue('phoneCall', '');
                    } else {
                      setValue('timeRange', ['09:00:00', '18:00:00']);
                    }

                    validateContactMethods();
                  }}
                />
                <span>Phone Call</span>
              </label>

              {selectedMethods.phoneCall && (
                <div className="mt-2 pl-6 space-y-3">
                  <AppPhoneInput name="phoneCall" control={control} />
                </div>
              )}
            </div>
          </div>

          <div className="mt-2 pl-2">
            <AppTimeRangePicker
              name="timeRange"
              control={control}
              label="Time"
              defaultValue={[dayjs('09:00:00', 'HH:mm:ss'), dayjs('18:00:00', 'HH:mm:ss')]}
            />
          </div>
        </form>
      </AppModal.Body>
    </Modal>
  );
};

export default PreferredContactMethodModal;
